import { supabase } from "@/integrations/supabase/client";
import type { AdCampaign, AdPackage, CreateCampaignInput } from "./types";

// Tables not yet present in generated types — use untyped client.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;
const tbl = (name: string) => db.from(name);

export const ads = {
  async listPackages(): Promise<AdPackage[]> {
    const { data, error } = await tbl("ad_packages")
      .select("*")
      .eq("active", true)
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data ?? []) as unknown as AdPackage[];
  },

  async listMyCampaigns(): Promise<AdCampaign[]> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) return [];
    const { data, error } = await tbl("ad_campaigns")
      .select("*")
      .eq("advertiser_id", user.id)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as unknown as AdCampaign[];
  },

  async listLiveForFeed(limit = 5): Promise<AdCampaign[]> {
    const nowIso = new Date().toISOString();
    const { data, error } = await tbl("ad_campaigns")
      .select("*")
      .eq("status", "active")
      .lte("starts_at", nowIso)
      .gte("ends_at", nowIso)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []) as unknown as AdCampaign[];
  },

  async listForAdmin(status?: string): Promise<AdCampaign[]> {
    let q = tbl("ad_campaigns").select("*").order("created_at", { ascending: false });
    if (status) q = q.eq("status", status);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as unknown as AdCampaign[];
  },

  async createDraft(input: CreateCampaignInput & { amount_cents: number }): Promise<AdCampaign> {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error("Not authenticated");
    const { data, error } = await tbl("ad_campaigns")
      .insert({
        advertiser_id: user.id,
        title: input.title,
        body: input.body ?? null,
        image_url: input.image_url ?? null,
        cta_label: input.cta_label ?? null,
        cta_url: input.cta_url ?? null,
        package_id: input.package_id,
        amount_cents: input.amount_cents,
        status: "pending_payment",
      })
      .select("*")
      .single();
    if (error) throw error;
    return data as unknown as AdCampaign;
  },

  async markPaid(campaignId: string, provider: string, ref: string): Promise<AdCampaign> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("ad_mark_paid", {
      _campaign_id: campaignId,
      _provider: provider,
      _ref: ref,
    });
    if (error) throw error;
    return data as AdCampaign;
  },

  async approve(campaignId: string): Promise<AdCampaign> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("ad_admin_approve", {
      _campaign_id: campaignId,
    });
    if (error) throw error;
    return data as AdCampaign;
  },

  async reject(campaignId: string, reason: string): Promise<AdCampaign> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("ad_admin_reject", {
      _campaign_id: campaignId,
      _reason: reason,
    });
    if (error) throw error;
    return data as AdCampaign;
  },

  async logEvent(campaignId: string, type: "impression" | "click"): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase.rpc as any)("ad_log_event", {
      _campaign_id: campaignId,
      _event_type: type,
    });
  },
};
